<template>
  <nav class="app-pagination" :class="alignClass" :aria-label="ariaLabel">
    <ul class="app-pagination__list" :class="sizeClass">
      <li v-if="!hideGotoEndButtons" class="app-pagination__item" :class="{ 'app-pagination__item--disabled': isFirstDisabled }">
        <button
          type="button"
          class="app-pagination__link"
          :disabled="isFirstDisabled"
          :aria-controls="ariaControls"
          :aria-label="labelFirstPage"
          @click="goTo(1)">
          {{ firstText }}
        </button>
      </li>
      <li class="app-pagination__item" :class="{ 'app-pagination__item--disabled': isFirstDisabled }">
        <button
          type="button"
          class="app-pagination__link"
          :disabled="isFirstDisabled"
          :aria-controls="ariaControls"
          :aria-label="labelPrevPage"
          @click="goTo(currentPage - 1)">
          {{ prevText }}
        </button>
      </li>

      <li v-if="paging.showFirstDots" class="app-pagination__item app-pagination__item--ellipsis" role="separator">
        <span class="app-pagination__link">{{ ellipsisText }}</span>
      </li>

      <li
        v-for="page in paging.pages"
        :key="page"
        class="app-pagination__item"
        :class="{ 'app-pagination__item--active': page === currentPage }">
        <button
          type="button"
          class="app-pagination__link"
          :disabled="disabled"
          :aria-controls="ariaControls"
          :aria-current="page === currentPage ? 'page' : null"
          :aria-label="`${labelPage} ${page}`"
          @click="goTo(page)">
          {{ page }}
        </button>
      </li>

      <li v-if="paging.showLastDots" class="app-pagination__item app-pagination__item--ellipsis" role="separator">
        <span class="app-pagination__link">{{ ellipsisText }}</span>
      </li>

      <li class="app-pagination__item" :class="{ 'app-pagination__item--disabled': isLastDisabled }">
        <button
          type="button"
          class="app-pagination__link"
          :disabled="isLastDisabled"
          :aria-controls="ariaControls"
          :aria-label="labelNextPage"
          @click="goTo(currentPage + 1)">
          {{ nextText }}
        </button>
      </li>
      <li v-if="!hideGotoEndButtons" class="app-pagination__item" :class="{ 'app-pagination__item--disabled': isLastDisabled }">
        <button
          type="button"
          class="app-pagination__link"
          :disabled="isLastDisabled"
          :aria-controls="ariaControls"
          :aria-label="labelLastPage"
          @click="goTo(paging.numberOfPages)">
          {{ lastText }}
        </button>
      </li>
    </ul>
  </nav>
</template>

<script>
/**
 * AppPagination
 *
 * Paginación con API compatible con bootstrap-vue <b-pagination>
 * (v-model / total-rows / per-page / limit / align / size / *-text,
 * eventos input y change), para reemplazarlo sin cambiar a los consumidores.
 */
import { buildPaginationPages, clampPage, getNumberOfPages } from '@/utils/paginationUtils'

export default {
  name: 'AppPagination',
  props: {
    // Página actual (v-model)
    value: {
      type: [Number, String],
      default: 1
    },
    totalRows: {
      type: [Number, String],
      default: 0
    },
    perPage: {
      type: [Number, String],
      default: 20
    },
    // Cantidad máxima de botones de página visibles (incluye las elipsis)
    limit: {
      type: [Number, String],
      default: 5
    },
    // left | center | right | fill
    align: {
      type: String,
      default: 'left'
    },
    // sm | md | lg
    size: {
      type: String,
      default: 'md'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hideGotoEndButtons: {
      type: Boolean,
      default: false
    },
    hideEllipsis: {
      type: Boolean,
      default: false
    },
    firstText: {
      type: String,
      default: '«'
    },
    prevText: {
      type: String,
      default: '‹'
    },
    nextText: {
      type: String,
      default: '›'
    },
    lastText: {
      type: String,
      default: '»'
    },
    ellipsisText: {
      type: String,
      default: '…'
    },
    ariaControls: {
      type: String,
      default: null
    },
    ariaLabel: {
      type: String,
      default: 'Paginación'
    },
    labelFirstPage: {
      type: String,
      default: 'Ir a la primera página'
    },
    labelPrevPage: {
      type: String,
      default: 'Ir a la página anterior'
    },
    labelNextPage: {
      type: String,
      default: 'Ir a la página siguiente'
    },
    labelLastPage: {
      type: String,
      default: 'Ir a la última página'
    },
    labelPage: {
      type: String,
      default: 'Ir a la página'
    }
  },
  computed: {
    numberOfPages () {
      return getNumberOfPages(this.totalRows, this.perPage)
    },
    currentPage () {
      return clampPage(this.value, this.numberOfPages)
    },
    paging () {
      return buildPaginationPages({
        currentPage: this.currentPage,
        numberOfPages: this.numberOfPages,
        limit: this.limit,
        hideEllipsis: this.hideEllipsis
      })
    },
    isFirstDisabled () {
      return this.disabled || this.currentPage <= 1
    },
    isLastDisabled () {
      return this.disabled || this.currentPage >= this.numberOfPages
    },
    alignClass () {
      return `app-pagination--${this.align}`
    },
    sizeClass () {
      return this.size === 'md' ? null : `app-pagination__list--${this.size}`
    }
  },
  methods: {
    goTo (page) {
      const target = clampPage(page, this.numberOfPages)
      if (this.disabled || target === this.currentPage) return
      // Igual que <b-pagination>: change solo por interacción del usuario.
      this.$emit('input', target)
      this.$emit('change', target)
    }
  }
}
</script>

<style scoped>
.app-pagination {
  display: flex;
}

.app-pagination--left {
  justify-content: flex-start;
}

.app-pagination--center {
  justify-content: center;
}

.app-pagination--right {
  justify-content: flex-end;
}

.app-pagination__list {
  display: flex;
  padding: 0;
  margin: 0;
  list-style: none;
  border-radius: 0.25rem;
}

.app-pagination--fill .app-pagination__list {
  width: 100%;
}

.app-pagination--fill .app-pagination__item {
  flex: 1 1 auto;
}

.app-pagination__item {
  display: flex;
}

.app-pagination__link {
  display: block;
  width: 100%;
  min-width: 2.25rem;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  font-size: var(--app-font-size-base);
  line-height: 1.25;
  text-align: center;
  color: var(--app-primary);
  background-color: var(--app-card-bg);
  border: 1px solid #dee2e6;
  cursor: pointer;
  transition: color 0.15s, background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.app-pagination__item:first-child .app-pagination__link {
  margin-left: 0;
  border-top-left-radius: 0.25rem;
  border-bottom-left-radius: 0.25rem;
}

.app-pagination__item:last-child .app-pagination__link {
  border-top-right-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

.app-pagination__link:hover:not(:disabled) {
  position: relative;
  z-index: 2;
  color: var(--app-secondary);
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.app-pagination__link:focus {
  position: relative;
  z-index: 3;
  outline: 0;
  box-shadow: 0 0 0 0.2rem var(--app-primary-focus);
}

.app-pagination__item--active .app-pagination__link {
  position: relative;
  z-index: 3;
  color: #fff;
  background-color: var(--app-primary);
  border-color: var(--app-primary);
  cursor: default;
}

.app-pagination__item--active .app-pagination__link:hover:not(:disabled) {
  color: #fff;
  background-color: var(--app-primary);
  border-color: var(--app-primary);
}

.app-pagination__item--disabled .app-pagination__link,
.app-pagination__item--ellipsis .app-pagination__link,
.app-pagination__link:disabled {
  color: #6c757d;
  background-color: var(--app-card-bg);
  border-color: #dee2e6;
  cursor: default;
}

.app-pagination__list--sm .app-pagination__link {
  min-width: 1.75rem;
  padding: 0.25rem 0.5rem;
  font-size: var(--app-font-size-sm);
  line-height: 1.5;
}

.app-pagination__list--lg .app-pagination__link {
  min-width: 3rem;
  padding: 0.75rem 1.5rem;
  font-size: var(--app-font-size-lg);
  line-height: 1.5;
}
</style>
